import React, {
    ChangeEvent,
    useContext,
    useEffect,
    useReducer,
    useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import AuthContext from '../../store/auth-context';
import styles from './Login.module.css';
import Button, { ButtonType } from '../general-components/Button';
import WelcomePage from '../ui-components/WelcomeLayout';
import FormInput from '../general-components/FormInput';
import Modal from '../manage-user/Modal';
import { emailValidate } from '../../services/formValidationService';
import RegionRedirectNotice from './RegionRedirectNotice';
import { isWrongRegionResponse } from '../../utils/regionRedirect';
import {
    formReducer,
    errorReducer,
    camelCaseToSentence,
} from '../../utils/utils';
import { Link, useSearchParams } from 'react-router-dom';

const initialFormData = {
    email: '',
    password: '',
};
// initialErrorData includes only required fields
const initialErrorData = { email: '', password: '' };

const Login = () => {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();
    const [regionRedirect, setRegionRedirect] = useState<{
        redirectTo: string;
        region: string;
    } | null>(null);

    const [formData, setFormData] = useReducer(formReducer, {
        ...initialFormData,
        email: searchParams.get('email'),
    });
    const [errorData, setErrorData] = useReducer(
        errorReducer,
        initialErrorData
    );
    const ctx = useContext(AuthContext);
    const [errorMessage, setErrorMessage] = useState('');
    const [loginLoading, setLoginLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [inactiveErrorMessage, setInactiveErrorMessage] = useState('');

    const requiredFields = Object.keys(initialErrorData);

    const handleInputChange = (
        e: ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => {
        const { name, value } = e.target;

        setFormData({
            type: 'update',
            name,
            value,
        });
        requiredFields.includes(name) &&
            setErrorData({ type: 'update', name, value: '' });
    };

    const submitLogin = () => {
        let allChecksPassed = false;
        const ev = emailValidate(formData.email);
        if (!ev.valid) {
            setErrorData({
                type: 'update',
                name: 'email',
                value: t('login.modal.errorMessages.invalidEmail'),
            });
        }
        Object.entries(formData).forEach(([key, value]) => {
            if (requiredFields.includes(key) && value === '') {
                setErrorData({
                    type: 'update',
                    name: key,
                    value: t('login.modal.errorMessages.fillField', {
                        field: camelCaseToSentence(key),
                    }),
                });
            }
        });

        allChecksPassed =
            Object.entries(formData)
                .filter(([key]) => requiredFields.includes(key))
                .every(([key, value]) => value !== '') && ev.valid;

        if (allChecksPassed) {
            setLoginLoading(true);
            ctx.onLogin(formData.email, formData.password).then((res: any) => {
                if (isWrongRegionResponse(res.status, res.data)) {
                    setRegionRedirect({
                        redirectTo: res.data.redirectTo,
                        region: res.data.region,
                    });
                    setLoginLoading(false);
                    return;
                }
                if (res.status === 401) {
                    const pw = formData.password.trim();
                    ctx.onLogin(formData.email, pw).then((res: any) => {
                        if (res.status === 401) {
                            setErrorMessage(
                                t(
                                    'login.modal.errorMessages.invalidCredentials'
                                )
                            );
                        }
                        setLoginLoading(false);
                    });
                } else {
                    if (res.status === 403) {
                        setInactiveErrorMessage(res.data.message);
                        setShowModal(true);
                    }
                    if (res.status === 400 || res.status === 402) {
                        setErrorMessage(res.data);
                    }
                    // Wrong region but no redirect target configured: say so.
                    if (res.status === 409) {
                        setErrorMessage(res.data?.message ?? res.data);
                    }
                    if (res.status === 429) {
                        setErrorMessage(res.data);
                    }
                    setLoginLoading(false);
                }
            });
        }
    };

    const inactiveReminder = {
        title: t('login.modal.title'),
        message: inactiveErrorMessage,
        className: 'Warning-modal',
        children: [<></>],
    };
    useEffect(() => {
        const listener = (event: any) => {
            if (event.code === 'Enter' || event.code === 'NumpadEnter') {
                event.preventDefault();
                submitLogin();
            }
        };
        document.addEventListener('keydown', listener);
        return () => {
            document.removeEventListener('keydown', listener);
        };
    }, [formData]);

    return (
        <WelcomePage>
            <React.Fragment>
                <div className={styles.info}>
                    <h3>{t('login.title')}</h3>
                    <p>{t('login.subtitle')}</p>
                </div>
                <form className={styles['login-form']}>
                    {regionRedirect && (
                        <RegionRedirectNotice
                            redirectTo={regionRedirect.redirectTo}
                            region={regionRedirect.region}
                            email={formData.email}
                        />
                    )}
                    {errorMessage && (
                        <div className={styles.alert}>
                            <p id="error-message">{errorMessage}</p>
                        </div>
                    )}
                    <FormInput
                        type="email"
                        name="email"
                        label={t('login.email.label')}
                        defaultValue={searchParams.get('email')}
                        placeholder={t('login.email.placeholder')}
                        onChange={handleInputChange}
                        errorMsg={errorData.email}
                    />
                    <div>
                        <FormInput
                            type="password"
                            name="password"
                            label={t('login.password.label')}
                            placeholder={t('login.password.placeholder')}
                            onChange={handleInputChange}
                            errorMsg={errorData.password}
                        />
                        <label className={styles.forgotPassword}>
                            {t('login.forgotPassword')}{' '}
                            <a href={'/initiate-reset'}>
                                {t('login.forgotPasswordLink')}
                            </a>{' '}
                            {t('login.forgotPasswordHelp')}
                        </label>
                    </div>
                    <Button
                        className={styles.button}
                        type={ButtonType.submit}
                        onClick={() => {
                            submitLogin();
                        }}
                        loading={loginLoading}
                    >
                        {t('login.loginButton')}
                    </Button>
                    {/* <label>
                        Not yet a user?{' '}
                        <a href="/sign-up/trial">Sign up here</a>
                    </label> */}
                </form>
                <div className={styles.linksWrapper}>
                    <div className={styles.links}>
                        <p className={styles.linksTitle}>
                            {t('login.getStarted.title')}
                        </p>
                        <div className={styles.highlightedText}>
                            <Link to="/sign-up/individual?enterprise=true">
                                {t('login.getStarted.pricing')}
                            </Link>
                        </div>
                    </div>
                    <div className={styles.links}>
                        <p className={styles.linksTitle}>
                            {t('login.links.title')}
                        </p>
                        <div className={styles.highlightedText}>
                            <Link to="https://nordinsight.notion.site/terms-conditions">
                                {t('login.links.terms')}
                            </Link>
                        </div>
                        <div className={styles.highlightedText}>
                            <Link to="https://nordinsight.notion.site/refund-policy">
                                {t('login.links.refund')}
                            </Link>
                        </div>
                        <div className={styles.highlightedText}>
                            <Link to="https://nordinsight.notion.site/privacy-plicy">
                                {t('login.links.privacy')}
                            </Link>
                        </div>
                    </div>
                </div>
                {showModal && (
                    <Modal
                        info={inactiveReminder}
                        updateView={() => {
                            setShowModal(!showModal);
                        }}
                    >
                        {inactiveReminder.children}
                    </Modal>
                )}
            </React.Fragment>
        </WelcomePage>
    );
};

export default Login;
